import { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { useLeadCapture } from '@/hooks/useLeadCapture';
import { useFormTracking, useConversionTracking } from '@/hooks/useAnalytics';
import { ContactFormPixel } from '@/components/analytics/ConversionPixel';

const initialFormData = {
  name: '', email: '', phone: '', company: '', projectType: '', location: '',
  propertySize: '', budgetRange: '', timeline: '', message: ''
};

const serviceTypes = [
  '3D Virtual Tours',
  'Construction Progress',
  'Legal Documentation',
  'Insurance Claims',
  'Digital Spatial Intelligence',
  'Before & After Scanning',
  'Custom Project'
];

const ContactForm = ({ compact = false }: { compact?: boolean }) => {
  const { toast } = useToast();
  const { captureLead, isSubmitting } = useLeadCapture();
  const [showConversionPixel, setShowConversionPixel] = useState(false);
  const formLocation = typeof window === 'undefined' ? '/contact' : window.location.pathname;
  const { trackFormStart, trackFormSubmit } = useFormTracking('contact_form', formLocation);
  const { trackContactFormSubmission } = useConversionTracking();
  const [formData, setFormData] = useState(initialFormData);
  const hasStarted = useRef(false);

  const handleFormStart = () => {
    if (hasStarted.current) return;
    hasStarted.current = true;
    trackFormStart();
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const success = await captureLead({
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      company: formData.company,
      project_type: formData.projectType,
      property_size: formData.propertySize,
      budget_range: formData.budgetRange,
      timeline: formData.timeline,
      message: [formData.location && `Property location: ${formData.location}`, formData.message].filter(Boolean).join('\n\n'),
      source_page: formLocation,
    });

    if (!success) {
      trackFormSubmit(false, 'Lead capture failed');
      return;
    }
    trackFormSubmit(true);
    trackContactFormSubmission({
      email: formData.email,
      phone: formData.phone,
      service: formData.projectType,
      message: formData.message,
      source: formLocation
    });
    toast({
      title: 'Quote request received',
      description: 'We’ll contact you within two business hours to discuss your project.'
    });
    setFormData(initialFormData);
    hasStarted.current = false;
    setShowConversionPixel(true);
  };

  const update = (field: string, value: string) => setFormData(current => ({ ...current, [field]: value }));

  return (
    <section className={compact ? 'py-0' : 'py-20'}>
      <div className={compact ? 'mx-auto max-w-2xl' : 'container mx-auto max-w-3xl px-4'}>
        {!compact && (
          <header className="mb-10 max-w-2xl">
            <p className="ed-kicker">Tell us about the property</p>
            <h2 className="mb-5 text-4xl font-bold text-foreground">Request a free quote.</h2>
            <p className="text-muted-foreground">Share the basics. We’ll follow up with pricing and the next available capture time.</p>
          </header>
        )}

        <form onSubmit={handleSubmit} onFocusCapture={handleFormStart} className="space-y-5">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div><Label htmlFor="name">Full Name *</Label><Input id="name" value={formData.name} onChange={event => update('name', event.target.value)} required autoComplete="name" /></div>
            <div><Label htmlFor="email">Email Address *</Label><Input id="email" type="email" value={formData.email} onChange={event => update('email', event.target.value)} required autoComplete="email" /></div>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div><Label htmlFor="phone">Phone Number</Label><Input id="phone" type="tel" value={formData.phone} onChange={event => update('phone', event.target.value)} autoComplete="tel" /></div>
            <div>
              <Label htmlFor="projectType">Service *</Label>
              <Select value={formData.projectType} onValueChange={value => update('projectType', value)} required>
                <SelectTrigger id="projectType"><SelectValue placeholder="Choose a service" /></SelectTrigger>
                <SelectContent>{serviceTypes.map(type => <SelectItem key={type} value={type}>{type}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label htmlFor="location">Property Location *</Label><Input id="location" value={formData.location} onChange={event => update('location', event.target.value)} required placeholder="City, State" autoComplete="street-address" /></div>
          <div><Label htmlFor="message">What do you need?</Label><Textarea id="message" value={formData.message} onChange={event => update('message', event.target.value)} placeholder="A short description is enough." rows={3} /></div>
          <Button type="submit" size="lg" className="w-full" disabled={isSubmitting}>{isSubmitting ? 'Sending request…' : 'Request free quote'}</Button>
          <p className="text-center text-sm text-muted-foreground">We’ll review your request and follow up within two business hours.</p>
        </form>
      </div>
      {showConversionPixel && <ContactFormPixel />}
    </section>
  );
};

export default ContactForm;
